import React, { useMemo } from "react";
import { Flex } from "../../../Flex";
import { Span } from "../../../Span";
import { List } from "../../../List";
import { ListItem } from "../../../ListItem";
import { IconPopupTip } from "../../../IconPopupTip";
import { useUserAuthentication } from "../../../../contexts/UserAuthenticationProvider/UserAuthenticationProvider";
import { ContactDetailsAndRole } from "../../../ContactDetailsForm/ContactDetailsAndRole";
import { ContactDetails } from "../../../../types/ContactDetailsValues";
import {
  ContactConfigBuilder,
  ContactPreferencesConfigBuilder,
} from "../../../ContactDetailsForm/ContactDetailsAndRole/_/utils/ContactConfigBuilder";

interface Props {
  id: string;
  contactDetails: ContactDetails[];
}

export const DisconnectionContactDetailsForm: React.FC<Props> = ({
  id,
  contactDetails,
}) => {
  const {
    authenticatedUserContext: { isLoggedIn },
  } = useUserAuthentication();

  const contactConfig = useMemo(() => {
    return [
      new ContactConfigBuilder()
        .withContactConfig({
          role: "Requester",
        })
        .withFieldsConfig({
          optionalAlternatePhone: true,
          optionalBusinessName: true,
          showBusinessName: true,
          disabledFirstName: isLoggedIn,
          disabledLastName: isLoggedIn,
          disabledEmail: isLoggedIn,
          disabledMobile: isLoggedIn,
          disabledAlternatePhone: isLoggedIn,
          disabledBusinessName: isLoggedIn,
          disableBusinessAddress: isLoggedIn,
        })
        .withPreferencesConfig([
          new ContactPreferencesConfigBuilder()
            .withConfig({ emailDisabled: true })
            .build(),
        ])
        .build(),
      new ContactConfigBuilder()
        .withContactConfig({
          role: "Quote payer",
          canAssignToAnother: true,
        })
        .withFieldsConfig({
          optionalAlternatePhone: true,
          optionalBusinessName: true,
          showBusinessName: true,
          showBusinessAddress: true,
        })
        .withPreferencesConfig([
          new ContactPreferencesConfigBuilder()
            .withConfig({
              contactPreferenceType: "quoteContactPreferences",
              title: "Quote update:",
              emailDisabled: true,
            })
            .build(),
          new ContactPreferencesConfigBuilder().build(),
        ])
        .build(),
      new ContactConfigBuilder()
        .withContactConfig({
          role: "Property owner",
          canAssignToAnother: true,
        })
        .withFieldsConfig({
          optionalMobile: true,
          optionalAlternatePhone: true,
          optionalBusinessName: true,
          showBusinessName: true,
        })
        .withPreferencesConfig([new ContactPreferencesConfigBuilder().build()])
        .build(),
      new ContactConfigBuilder()
        .withContactConfig({
          role: "Site contact",
          canAssignToAnother: true,
        })
        .withFieldsConfig({
          optionalEmail: true,
          optionalAlternatePhone: true,
          optionalBusinessName: true,
          showBusinessName: true,
        })
        .withPreferencesConfig([new ContactPreferencesConfigBuilder().build()])
        .build(),
    ];
  }, [isLoggedIn]);

  return (
    <Flex flexDirection="column" id={id} mb={4}>
      <Flex mt={0} mb={4}>
        <Span fontSize={4} lineHeight={4}>
          Contact details
        </Span>
        <IconPopupTip icon="help" ml={["auto", "auto", "auto", 3]}>
          {
            "We require the contact details of all people / roles, as we may need to contact them at different times during the job. Sometimes one person fulfils several roles e.g. the requester and the property owner may be the same person."
          }
        </IconPopupTip>
      </Flex>
      <Span lineHeight={2} mt={0} mb={4}>
        We require contact information for:
        <List mb={0}>
          <ListItem>
            <Span fontWeight={"bold"}>the requester</Span>
            <Span>
              {" "}
              - the person who is authorised by the property owner to request
              the permanent disconnection
            </Span>
          </ListItem>
          <ListItem>
            <Span fontWeight={"bold"}>the quote payer</Span>
            <Span>
              {" "}
              - the person who will be paying for any costs incurred to complete
              the permanent disconnection
            </Span>
          </ListItem>
          <ListItem>
            <Span fontWeight={"bold"}>the property owner</Span>
            <Span>
              {" "}
              - the person who holds legal ownership of the property and is
              authorising the permanent disconnection
            </Span>
          </ListItem>
          <ListItem>
            <Span fontWeight={"bold"}>the site contact</Span>
            <Span>
              {" "}
              - the person designated as the primary contact for access or
              addressing any issues or concerns whilst our crew are on site
            </Span>
          </ListItem>
        </List>
      </Span>

      <ContactDetailsAndRole
        idPrefix={`${id}`}
        namePrefix={"contactDetails"}
        contactConfigs={contactConfig}
        contactDetails={contactDetails}
      />
    </Flex>
  );
};
